/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { cssMap, jsx } from '@atlaskit/css';
import SuitcaseIcon from '@atlaskit/icon/core/briefcase';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { token } from '@atlaskit/tokens';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { isForgeAgent } from '../../../../../common/types.ts';
import { type AgentDetailsWithAggData } from '../../../../../controllers/agent-details/index.ts';
import {
	isKnowledgeScopeEnabled,
	useKnowledgeProductsRender,
} from '../../../../../controllers/knowledge-products-rendering/index.tsx';
import { AgentKnowledgeSection } from '../../../../../controllers/knowledge-section/index.tsx';
import { useTenantDataController } from '../../../../../controllers/tenant-data/index.tsx';

import { type agentAbout_AtlassianConversationAssistantAgent_AgentAboutCard$key } from './__generated__/agentAbout_AtlassianConversationAssistantAgent_AgentAboutCard.graphql.ts';
import { ActionsSection } from './actions-section/index.tsx';
import messages from './messages.ts';
import { TeamsSection } from './teams-section/index.tsx';

const styles = cssMap({
	wrapper: {
		boxShadow: token('elevation.shadow.raised'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		marginTop: token('space.300'),
		gap: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
	},

	heading: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		font: token('font.body.small'),
		fontWeight: token('font.weight.semibold'),
		color: token('color.text.subtlest'),
		marginBlockStart: token('space.0'),
		marginBlockEnd: token('space.100'),
	},
});

const InfoSection = ({ label, content }: { label: string; content: React.ReactNode }) => {
	return (
		<Box>
			<Box as="h3" xcss={styles.heading}>
				{label}
			</Box>
			{content}
		</Box>
	);
};

export const AgentAboutWrapper = ({ children }: { children: React.ReactNode }): JSX.Element => {
	return <Box xcss={styles.wrapper}>{children}</Box>;
};

export const AgentAboutSection = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<InfoSection
			label={formatMessage(messages.aboutLabel)}
			content={
				<Inline space="space.100" alignBlock="center">
					<SuitcaseIcon color="currentColor" label="" />
					{formatMessage(messages.rovoAgentLabel)}
				</Inline>
			}
		/>
	);
};

export const AgentAboutCard = ({
	agent,
	assistanceServiceParams,
	scenarioListRef,
}: {
	agent: AgentDetailsWithAggData;
	scenarioListRef: agentAbout_AtlassianConversationAssistantAgent_AgentAboutCard$key | null;
} & AssistanceServiceInputParam): JSX.Element => {
	di(useTenantDataController, fg);
	const { formatMessage } = useIntl();
	const { data: tenantData, isFetching } = useTenantDataController(assistanceServiceParams.siteId);
	const shouldDisplayAgentKnowledgeCards = tenantData && !isFetching;
	const knowledgeScopeEnabled = isKnowledgeScopeEnabled(agent);

	const { getKnowledgeRenderForAgent } = useKnowledgeProductsRender({ assistanceServiceParams });

	const scenarioListFragment =
		useFragment<agentAbout_AtlassianConversationAssistantAgent_AgentAboutCard$key>(
			graphql`
				fragment agentAbout_AtlassianConversationAssistantAgent_AgentAboutCard on AgentStudioScenariosResult {
					...knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioListRef
				}
			`,
			scenarioListRef,
		);

	const isDeepResearchEnabled = agent.scenarios?.some((scenario) => scenario.isDeepResearchEnabled);
	const knowledgeRender = useMemo(
		() => getKnowledgeRenderForAgent({ agent, cloudUrl: tenantData?.cloudUrl }),
		[agent, getKnowledgeRenderForAgent, tenantData?.cloudUrl],
	);

	return (
		<AgentAboutWrapper>
			<AgentAboutSection />
			{shouldDisplayAgentKnowledgeCards && (
				<InfoSection
					label={formatMessage(messages.knowledgeLabel)}
					content={
						<AgentKnowledgeSection
							{...knowledgeRender.knowledgeSectionProps}
							currentCloudId={assistanceServiceParams.siteId}
							isForgeAgent={
								fg('rovo_agent_support_a2a_avatar')
									? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
									: isForgeAgent(agent)
							}
							knowledgeProductsRender={knowledgeRender.knowledgeProductsRender}
							knowledgeScopeEnabled={knowledgeScopeEnabled}
							isDeepResearchEnabled={isDeepResearchEnabled}
							scenarioListRef={scenarioListFragment ?? null}
						/>
					}
				/>
			)}
			<InfoSection
				label={formatMessage(
					fg('agent_studio_atomic_skills_rename') ? messages.toolsLabel : messages.skillsLabel,
				)}
				content={<ActionsSection agent={agent} />}
			/>

			<InfoSection
				label={formatMessage(messages.teamsLabel)}
				content={<TeamsSection agent={agent} />}
			/>
		</AgentAboutWrapper>
	);
};
